/**
 * @fileoverview Stripe Connect Account Management Hook
 * 
 * Manages Stripe Connect accounts for vendor organizations to receive payouts.
 * Stripe Connect enables the platform to send funds directly to vendors' bank
 * accounts while maintaining platform control and compliance.
 * 
 * Stripe Connect Setup Flow:
 * 1. Vendor initiates account creation through platform UI
 * 2. Edge function creates Stripe Connect Express account
 * 3. Vendor is redirected to Stripe's hosted onboarding flow
 * 4. Vendor completes identity verification and bank account setup
 * 5. Stripe webhook confirms account activation
 * 6. Vendor can now receive payouts through the platform
 * 
 * Account Capabilities Required:
 * - charges_enabled: Can receive payments
 * - payouts_enabled: Can receive transfers to bank account
 * - onboarding_completed: Finished Stripe's verification process
 * 
 * Key Features:
 * - Check vendor's Stripe Connect status
 * - Initiate new Connect account setup
 * - Refresh account status from Stripe
 * - Handle onboarding completion
 * 
 * @module useStripeConnect
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for managing a vendor organization's Stripe Connect account
 * 
 * Provides functionality to check Connect account status, create new accounts,
 * and refresh account information from Stripe. Essential for vendors to receive
 * payouts from completed jobs.
 * 
 * Account States:
 * - Not Connected: No Stripe account exists (need to create)
 * - Pending: Account created but onboarding incomplete
 * - Connected: Fully verified and can receive payouts
 * 
 * @param organizationId - The UUID of the vendor organization
 * @returns Object with account data, connection status, and mutation functions
 * 
 * @example
 * ```tsx
 * function VendorStripeSetup({ organizationId }) {
 *   const {
 *     stripeAccount,
 *     isConnected,
 *     isPending,
 *     isLoading,
 *     createConnectAccount,
 *     refreshAccountStatus
 *   } = useStripeConnect(organizationId);
 * 
 *   const handleSetup = () => {
 *     createConnectAccount.mutate({
 *       email: 'vendor@example.com',
 *       refreshUrl: `${window.location.origin}/vendor/stripe-refresh`,
 *       returnUrl: `${window.location.origin}/vendor/stripe-complete`
 *     });
 *     // User will be redirected to Stripe
 *   };
 * 
 *   if (isConnected) {
 *     return <Badge>Connected - Ready to receive payouts</Badge>;
 *   }
 * 
 *   if (isPending) {
 *     return (
 *       <div>
 *         <p>Onboarding in progress...</p>
 *         <button onClick={() => refreshAccountStatus.mutate()}>
 *           Check Status
 *         </button>
 *       </div>
 *     );
 *   }
 * 
 *   return (
 *     <button onClick={handleSetup}>
 *       Setup Stripe Connect
 *     </button>
 *   );
 * }
 * ```
 */
export const useStripeConnect = (organizationId: string) => {
  const { toast } = useToast();

  const { data: stripeAccount, isLoading } = useQuery({
    queryKey: ['stripe-account', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stripe_accounts')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!organizationId,
  });

  const createConnectAccount = useMutation({
    mutationFn: async ({ email, refreshUrl, returnUrl }: { 
      email: string; 
      refreshUrl: string; 
      returnUrl: string; 
    }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await invokeEdgeFunction('create-stripe-connect-account', {
        body: {
          organizationId,
          email,
          refreshUrl,
          returnUrl,
        },
        callerOperation: 'useStripeConnect.createConnectAccount',
      });

      if (error) throw error;
      return data as { url?: string };
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: 'Setup Failed',
          description: 'Could not initialize Stripe Connect. Please refresh the page and try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Unknown error occurred';
      toast({
        title: 'Stripe Connect Failed',
        description: `${errorMessage}. Please try again or contact support if the issue persists.`,
        variant: 'destructive',
      });
      console.error('Error creating Stripe account:', error);
    },
  });

  const refreshAccountStatus = useMutation({
    mutationFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await invokeEdgeFunction('refresh-stripe-account-status', {
        body: { organizationId },
        callerOperation: 'useStripeConnect.refreshAccountStatus',
      });

      if (error) throw error;
      return data as { success?: boolean; needsSetup?: boolean };
    },
    onSuccess: (data) => {
      if (data?.success) {
        toast({
          title: 'Status Refreshed',
          description: 'Stripe account status has been updated.',
        });
        // Refetch the account data
        window.location.reload();
      } else if (data?.needsSetup) {
        toast({
          title: 'Setup Required',
          description: 'No Stripe account found. Please complete the setup first.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Refresh Failed',
        description: error?.message || 'Could not refresh account status',
        variant: 'destructive',
      });
      console.error('Error refreshing Stripe account:', error);
    },
  });

  const createLoginLink = useMutation({
    mutationFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await invokeEdgeFunction('create-stripe-login-link', {
        body: { organizationId },
        callerOperation: 'useStripeConnect.createLoginLink',
      });

      if (error) throw error;
      return data as { url?: string };
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        toast({
          title: 'Dashboard Access Failed',
          description: 'Could not generate dashboard link. Please try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Dashboard Access Failed',
        description: error?.message || 'Could not open Stripe dashboard',
        variant: 'destructive',
      });
      console.error('Error creating Stripe login link:', error);
    },
  });

  const isConnected = stripeAccount?.onboarding_completed && 
    stripeAccount?.charges_enabled && 
    stripeAccount?.payouts_enabled;
  const isPending = stripeAccount && !isConnected;

  return {
    stripeAccount,
    isLoading,
    isConnected,
    isPending,
    createConnectAccount,
    refreshAccountStatus,
    createLoginLink,
  };
};
